import { useCallback, useEffect, useMemo, useState } from "react";
import { Grid, Skeleton } from "@mui/material";
import { GridPaginationModel } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import GraphMap from "../../../../../components/charts/GraphMap";
import MarkerLayer from "../../../../../components/charts/GraphMap/layers/MarkerLayer";
import { calcMapConfig } from "../../../../../components/charts/GraphMap/utils";
import MultiValueWidget from "../../../../../components/charts/MultiValueWidget";
import { View } from "../../../../../components/layout/View";
import { PageType } from "../../../../../config/page.types";
import { useBreadcrumbs } from "../../../../../context/BreadcrumbsProvider";
import { useFiltersRegistry } from "../../../../../context/FiltersProvider";
import {
	ConsumptionPageDTO,
	ConsumptionPageItemDTO,
	NodesPageItemDTO,
} from "../../../../../datasources/api-client/data-contracts";
import { EmissionsTypesColors, ThemeColors } from "../../../../../theme/themeValues";
import ConsumptionsAndEmissions from "./components/ConsumptionsAndEmissions";
import { displayValues } from "./data";
import { useOperationalUnitsData } from "./useOperationalUnitsGreenrouterDetail";

export type SourcesTypes = {
	electricity: ConsumptionPageDTO;
	fuel: ConsumptionPageDTO;
	refrigerants: ConsumptionPageDTO;
	processGas: ConsumptionPageDTO;
	heatSteam: ConsumptionPageDTO;
	water: ConsumptionPageDTO;
};

// Chart colors
const chartColors = {
	electricity: EmissionsTypesColors.LightElectricity,
	fuel: EmissionsTypesColors.LightFuel,
	heatSteam: EmissionsTypesColors.LightHeat,
	refrigerants: EmissionsTypesColors.LightRefrigerant,
	processGas: EmissionsTypesColors.LightProcessGases,
	water: EmissionsTypesColors.LightWater,
};

const OperationalUnitsGreenrouterDetail = () => {
	const { t } = useTranslation("feature.units");
	const params = useParams();
	const { visitPage } = useBreadcrumbs();

	// Filters and pagination
	const { appliedFilters, registry } = useFiltersRegistry();

	const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
		page: 0,
		pageSize: 10,
	});
	const payload = useMemo(() => ({ ...appliedFilters.payload }), [appliedFilters]);
	// Fetch data
	const id = params.id || "";

	const { dataNode, consumption, isLoading, reload } = useOperationalUnitsData({
		payload,
		id,
		paginationModel,
	});

	useEffect(() => {
		if (id) {
			visitPage(PageType.ResultsOperationalUnitsGreenRouterDetail, false, dataNode.data?.code, id);
		}
	}, [id, visitPage]);

	// Tabs setup
	const sourceKeys = Object.keys(consumption.sources) as (keyof SourcesTypes)[];
	const [selectedKey, setSelectedKey] = useState<keyof SourcesTypes>(sourceKeys[0]);
	const selectedData = consumption.sources[selectedKey];

	// Handle tab switch
	const handleSelectKeyChange = (newValue: keyof SourcesTypes) => {
		setSelectedKey(newValue);
		// Reset pagination on tab change
		setPaginationModel({ page: 0, pageSize: 10 });
	};

	// Handle pagination change
	const handlePaginationChange = useCallback((newModel: GridPaginationModel) => {
		setPaginationModel(newModel);
	}, []);

	// Chart data setup
	const chartData = useMemo(() => {
		if (!selectedData?.items) return [];

		// Electricity has multiple value fields
		if (selectedKey === "electricity") {
			return selectedData.items.map((item: ConsumptionPageItemDTO) => ({
				label: item.elementLabel || item.id,
				value: item.renewable.value + item.selfProducedRenewable.value + item.selfProducedNonRenewable.value, // is needed for a total value showed on the tooltip
				renewable: item.renewable.value,
				selfProducedRenewable: item.selfProducedRenewable.value,
				selfProducedNonRenewable: item.selfProducedNonRenewable.value,
			}));
		}

		// Other sources use a single consumption field
		return selectedData.items.map((item: ConsumptionPageItemDTO) => ({
			label: item.elementLabel || item.id,
			consumption: item.consumption.value,
		}));
	}, [selectedData, selectedKey]);

	const chartColor = chartColors[selectedKey];
	const chartMeasure = selectedData?.items?.[0]?.consumption?.measure ?? "";

	// Extract display values for widgets
	const { personalInformation, generalData, totalEmissions, appliedFiltersToDetail } = displayValues(
		dataNode.data,
		t,
		appliedFilters.payload,
		registry
	);

	const nodeMapPosition: NodesPageItemDTO = {
		id: dataNode.data?.id || "",
		typeId: dataNode.data?.typeId || 0,
		typeDescription: dataNode.data?.description || "",
		code: dataNode.data?.code || "",
		description: dataNode.data?.description || "",
		lat: dataNode.data?.lat || 0,
		lng: dataNode.data?.lng || 0,
	};

	// Get maps info position
	const mapConfig = calcMapConfig([nodeMapPosition]);

	const headerContext = {
		refresh: reload,
		loading: isLoading,
	};

	return (
		<View pageType={PageType.ResultsOperationalUnitsGreenRouterDetail} headerContext={headerContext}>
			<Grid container spacing={2}>
				{/* Total emissions widget */}
				<Grid size={12} minHeight={120}>
					<MultiValueWidget
						isLoading={dataNode.isLoading}
						values={totalEmissions}
						valueVariant="body1"
						title={t("greenrouterDetail/total emissions from operational unit")}
						elementsPerRow={3}
					/>
				</Grid>

				{/* Personal information widget */}
				<Grid size={12} minHeight={200}>
					<MultiValueWidget
						isLoading={dataNode.isLoading}
						values={personalInformation}
						valueVariant="body1"
						title={t("greenrouterDetail/personal information")}
						elementsPerRow={5}
					/>
				</Grid>

				{/* General data widget */}
				<Grid size={12} minHeight={200}>
					<MultiValueWidget
						isLoading={dataNode.isLoading}
						values={generalData}
						valueVariant="body1"
						title={t("greenrouterDetail/general data")}
						elementsPerRow={4}
					/>
				</Grid>

				{appliedFiltersToDetail.length > 0 && (
					<Grid size={12} minHeight={150}>
						<MultiValueWidget
							isLoading={dataNode.isLoading}
							values={appliedFiltersToDetail}
							valueVariant="body1"
							title={t("applied filters", { ns: "common" })}
							elementsPerRow={5}
							areFilters={true}
						/>
					</Grid>
				)}

				<Grid size={12} minHeight={350}>
					{dataNode.isLoading ? (
						<Skeleton variant="rounded" height={"100%"} width={"100%"} />
					) : (
						<GraphMap center={mapConfig.center} tileProvider="draw" zoom={3} maxZoom={14} minZoom={3}>
							<MarkerLayer
								nodes={dataNode?.data ? [dataNode?.data] : undefined}
								color={ThemeColors.LightPrimary}
							/>
						</GraphMap>
					)}
				</Grid>

				{/* Consumptions and emissions section */}
				<Grid size={12} minHeight={400}>
					<ConsumptionsAndEmissions
						loading={consumption.isLoading}
						reload={consumption.reload}
						selectedData={selectedData}
						selectedKey={selectedKey}
						sourceKeys={sourceKeys}
						paginationModel={paginationModel}
						chartData={chartData}
						chartColor={chartColor}
						chartMeasure={chartMeasure}
						handleSelectKeyChange={handleSelectKeyChange}
						handlePaginationChange={handlePaginationChange}
					/>
				</Grid>
			</Grid>
		</View>
	);
};

export default OperationalUnitsGreenrouterDetail;
